import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { CORE_CONFIG_TOKEN } from '@ndk/ng-core/component/core.config';
import { NDDGridService } from '@ndk/ng-grid';
import { NDDTranslationService } from '@ndk/ng-translation';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';

import { ICoreConfigInternal } from '@core/core.config.model';
import { LoggerService } from '@core/logger/logger.service';
import { LocalStorageService } from '@ndk/ng-storage';
import { NDDGridWithCurrentLanguageService } from '@shared/ndd-ng-grid/ndd-ng-grid-with-current-language.service';

import { IUserAccess } from './user-access.model';
import { IUserManagerPermissionPolicies, IUserManagerPermissionPoliciesUpdateCommand } from './user-manager-permission-policies.model';
import { IUserManagerPermissionQuotas, IUserManagerPermissionQuotasUpdateCommand } from './user-manager-permission-quotas.model';
import { IUserSecureRelease } from './user-secure-release.model';
import { IUser, IUserRemoveCommand } from './user.model';

@Injectable()
export class UserGridService extends NDDGridWithCurrentLanguageService {
    constructor(http: HttpClient, @Inject(CORE_CONFIG_TOKEN) config: ICoreConfigInternal, localStorage: LocalStorageService) {
        super(http, `${ config.apiEndpoint }odata/users`, localStorage);
    }
}

@Injectable()
export class UserManagedAccountGridService extends NDDGridService {
    constructor(http: HttpClient, @Inject(CORE_CONFIG_TOKEN) config: ICoreConfigInternal) {
        super(http, `${ config.apiEndpoint }odata/managed-accounts`);
    }
}

@Injectable()
export class GroupsFromUsersGridService extends NDDGridService {
    constructor(http: HttpClient, @Inject(CORE_CONFIG_TOKEN) config: ICoreConfigInternal) {
        super(http, `${ config.apiEndpoint }odata/groups-from-user`);
    }
}

@Injectable()
export class UserService {
    private apiUrl: string;

    constructor(
        @Inject(CORE_CONFIG_TOKEN) config: ICoreConfigInternal,
        private http: HttpClient,
        private loggerService: LoggerService,
        private translation: NDDTranslationService) {
        this.apiUrl = `${ config.apiEndpoint }api/users`;
    }

    public createUser(user: IUser): Observable<number> {
        return this.http.post<number>(this.apiUrl, user)
            .pipe(tap(() => this.loggerService.success(true, [this.translation.instant('360RegisterSuccess')])));
    }

    public updateUser(user: IUser): Observable<boolean> {
        return this.http.post<boolean>(`${ this.apiUrl }/update`, user)
            .pipe(tap(() => this.loggerService.success(true, [this.translation.instant('360UpdateSuccess')])));
    }

    public checkRemoveUser(userId: number): Observable<boolean> {
        return this.http.get<boolean>(`${ this.apiUrl }/${ userId }/check-remove`);
    }

    public removeUser(command: IUserRemoveCommand): Observable<boolean> {
        return this.http.post<boolean>(`${ this.apiUrl }/remove`, command)
            .pipe(tap(() => this.loggerService.success(true, [this.translation.instant('360UserDeleted')])));
    }

    public get(id: number): Observable<IUser> {
        return this.http.get<IUser>(`${ this.apiUrl }/${ id }/properties`);
    }

    public getSecureRelease(id: number): Observable<IUserSecureRelease> {
        return this.http.get<IUserSecureRelease>(`${ this.apiUrl }/${ id }/secure-release`);
    }

    public removePolicyFromUser(command: object): Observable<boolean> {
        return this.http.post<boolean>(`${ this.apiUrl }/remove-policy`, command);
    }

    public addPolicyInUser(command: object): Observable<boolean> {
        return this.http.post<boolean>(`${ this.apiUrl }/add-policy`, command);
    }

    public getUserAccess(id: number): Observable<IUserAccess> {
        return this.http.get<IUserAccess>(`${ this.apiUrl }/${ id }/access`);
    }

    public updateUserAccess(model: IUserAccess): Observable<boolean> {
        return this.http.post<boolean>(`${ this.apiUrl }/access`, model);
    }

    public isManager(): Observable<boolean> {
        return this.http.get<boolean>(`${ this.apiUrl }/is-manager`);
    }

    public isManagerById(id: number): Observable<boolean> {
        return this.http.get<boolean>(`${ this.apiUrl }/${ id }/is-manager`);
    }

    /** Busca administração de cotas do **usuário autenticado**. */
    public getUserManagerPermissionQuotas(): Observable<IUserManagerPermissionQuotas> {
        return this.http.get<IUserManagerPermissionQuotas>(`${ this.apiUrl }/access/manager/quotas`);
    }

    /** Busca administração de políticas do **usuário autenticado**. */
    public getUserManagerPermissionPolicies(): Observable<IUserManagerPermissionPolicies> {
        return this.http.get<IUserManagerPermissionPolicies>(`${ this.apiUrl }/access/manager/policies`);
    }

    /** Busca administração de cotas do **usuário indicado por id**. */
    public getUserManagerPermissionQuotasById(id: number): Observable<IUserManagerPermissionQuotas> {
        return this.http.get<IUserManagerPermissionQuotas>(`${ this.apiUrl }/${ id }/access/manager/quotas`);
    }

    /** Busca administração de políticas do **usuário indicado por id**. */
    public getUserManagerPermissionPoliciesById(id: number): Observable<IUserManagerPermissionPolicies> {
        return this.http.get<IUserManagerPermissionPolicies>(`${ this.apiUrl }/${ id }/access/manager/policies`);
    }

    public updateUserManagerPermissionQuotas(command: IUserManagerPermissionQuotasUpdateCommand): Observable<boolean> {
        return this.http.post<boolean>(`${ this.apiUrl }/access/manager/quotas`, command);
    }

    public updateUserManagerPermissionPolicies(command: IUserManagerPermissionPoliciesUpdateCommand): Observable<boolean> {
        return this.http.post<boolean>(`${ this.apiUrl }/access/manager/policies`, command);
    }

    public isOwnEditing(id: number): Observable<boolean> {
        return this.http.get<boolean>(`${ this.apiUrl }/${ id }/is-own-access-editing`);
   }
}
